// src/pages/customerRejectedRecord/index.js
import { fetchCustomerRejectedList } from "./CustomerRejectedThunk";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  App,
  Button,
  Card,
  Modal,
  Input,
  message as antMessage,
  Typography,
  Grid,
  Tooltip,
  Drawer,
  Flex
} from "antd";
import { PrinterOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import CustomerRejectedFilters from "./components/CustomerRejectedFilters";
import CustomerRejectedTable from "./components/CustomerRejectedTable";
import ReportDom from "../../components/common/ReportDom";
import {
  customerStatusChangeApi,
  getConversationApi,
  saveConversationApi,
} from "../../apiservice/crm";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Title } = Typography;

const CustomerRejectedRecord = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { filters, list, loading } = useSelector(
    (state) => state.customerRejected
  );

  const [entryModal, setEntryModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [conversationText, setConversationText] = useState("");
  const [conversations, setConversations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const reportRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCustomerRejectedList({}));
  }, [dispatch]);

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: "Rejected Customers Report",
    removeAfterPrint: true,
  });

  const handleAdd = (customer) => {
    setSelectedCustomer(customer);
    setEntryModal(true);
  };

  const handleView = async (customer) => {
    setSelectedCustomer(customer);
    setConversations([]);
    setViewModal(true);
    try {
      const res = await getConversationApi({ customer_id: customer.id });
      setConversations(res.data || []);
    } catch (err) {
      antMessage.error("Failed to load conversation ❌");
    }
  };

  const handleAction = async (customer, status) => {
    Modal.confirm({
      title: `Are you sure you want to ${
        status === "a" ? "Approve" : "Pending"
      } ${customer.acc_name}?`,
      onOk: async () => {
        try {
          await customerStatusChangeApi({ customerId: customer.id, status });
          dispatch(fetchCustomerRejectedList(filters));
          antMessage.success("Action completed successfully ✅");
        } catch (err) {
          antMessage.error("Failed to perform action ❌");
        }
      },
    });
  };

  const handleSaveConversation = async () => {
    if (!conversationText.trim()) {
      antMessage.warning("Conversation text is required");
      return;
    }
    setSaving(true);
    try {
      await saveConversationApi({
        customer_id: selectedCustomer.id,
        text: conversationText,
      });
      setConversationText("");
      setEntryModal(false);
      antMessage.success("Conversation saved ✅");
    } catch (err) {
      antMessage.error("Failed to save conversation ❌");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <App>
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <Flex justify="space-between" align="center">
              <Title level={4} style={{ margin: 0 }}>
                Rejected Customers List
              </Title>

              <Tooltip title="Help">
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
                  onClick={() => setHelpOpen(true)}
                />
              </Tooltip>
            </Flex>
          }
          extra={
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              { !screens.xs && "Print" }
            </Button>
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body: { padding: 0 },
          }}
        />

        <CustomerRejectedFilters messageApi={message} />

        <Card className="shadow-md rounded-xl">
          <CustomerRejectedTable
            list={list}
            loading={loading}
            onAdd={handleAdd}
            onView={handleView}
            onApprove={(c) => handleAction(c, "a")}
            onPending={(c) => handleAction(c, "p")}
          />
        </Card>
      </App>

      {/* Hidden Print */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Rejected Customers Report"
          mode="report-page"
          filters={filters}
          renderTable={() => <CustomerRejectedTable list={list} printMode />}
        />
      </div>

      {/* Add Conversation Modal */}
      <Modal
        open={entryModal}
        title={`Add Conversation - ${selectedCustomer?.acc_name}`}
        onCancel={() => setEntryModal(false)}
        footer={null}
      >
        <TextArea
          rows={6}
          value={conversationText}
          onChange={(e) => setConversationText(e.target.value)}
          placeholder="Enter conversation..."
        />
        <Button
          type="primary"
          loading={saving}
          onClick={handleSaveConversation}
          block
          style={{ marginTop: "10px" }}
        >
          Save
        </Button>
      </Modal>

      {/* View Conversation Modal */}
      <Modal
        open={viewModal}
        title={`Conversation History - ${selectedCustomer?.acc_name}`}
        onCancel={() => setViewModal(false)}
        footer={null}
      >
        {conversations.length ? (
          conversations.map((c, i) => (
            <div
              key={i}
              style={{ marginBottom: "10px", whiteSpace: "pre-line" }}
            >
              <b>{i + 1}.</b> {c.text || c.conversation} <br />
              <small>{dayjs(c.created_date).format("DD MMM YYYY")}</small>
            </div>
          ))
        ) : (
          <p>No conversation found</p>
        )}
      </Modal>
      <Drawer
        title="Rejected Customers Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            Rejected Customer Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px", marginLeft:17 }}>
            This section helps manage rejected customer records,
            customer conversations, follow-ups, and CRM review workflows
            for customers that were not approved.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View all rejected customer records</li>
              <li>Move rejected customers back to pending review</li>
              <li>Approve rejected customers when required</li>
              <li>Add customer follow-up conversations and notes</li>
              <li>View complete customer conversation history</li>
              <li>Track rejection and review activities</li>
              <li>Filter and search rejected customer records</li>
              <li>Print rejected customer reports</li>
              <li>Maintain customer communication tracking</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Rejected customers can be moved back to pending status.</li>
              <li>Approved customers move into active CRM customer records.</li>
              <li>Conversation history helps track customer follow-up activities.</li>
              <li>All customer notes are stored with activity dates.</li>
              <li>Customer communication records improve future follow-ups.</li>
              <li>Printed reports help management review rejected customer records.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CustomerRejectedRecord;
